import { MAX_DESCRIPTION_LENGTH, MAX_TITLE_LENGTH, MIN_TITLE_LENGTH } from '@opengov/stories-shared/constants';
import { IPublication } from '@opengov/stories-shared/types';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import React, { useMemo, useState } from 'react';

import { publicationView as publicationViewLocale } from '../../../../locale/stories.en.locale.json';
import { IPublicationModalMessages } from '../../../../types';
import {
  useUpdatePublication,
  useUpdatePublicationUpdatedAt
} from '../PublicationView/hooks/usePublicationMutations';

import { styles } from './UpdatePublicationModal.styles';

export interface IUpdatePublicationModalProps {
  isAdmin: boolean;
  entityId: number;
  publicationModalMessages: IPublicationModalMessages;
  publication: IPublication;
  userId: number;
  onClose(): void;
}

export const UpdatePublicationModal: React.FC<IUpdatePublicationModalProps> = (props) => {
  const { publicationModalMessages, publication, onClose, isAdmin, entityId, userId } = props;
  const [publicationTitle, setPublicationTitle] = useState(publication.title);
  const [publicationDescription, setPublicationDescription] = useState(publication.description);
  const trimmedPublicationTitle = useMemo(() => publicationTitle.trim(), [publicationTitle]);
  const trimmedPublicationDescription = useMemo(() => publicationDescription.trim(), [publicationDescription]);

  // Write hooks live here now (no parent callback). Mirrors the
  // retired wrapper — chained updatePublicationUpdatedAt on success, errors
  // swallowed so the close-on-confirm UX is unchanged (hook toasts the error).
  const updatePublicationMutation = useUpdatePublication(
    { title: publicationViewLocale.somethingWentWrongError, body: publicationViewLocale.failedToUpdatePublication },
    publicationViewLocale.noPublicationSameName
  );
  const updateTimestampMutation = useUpdatePublicationUpdatedAt();

  const handleConfirmUpdatePublication = async () => {
    onClose();
    try {
      await updatePublicationMutation.mutateAsync({
        publicationId: publication.id,
        entityId: entityId,
        title: trimmedPublicationTitle,
        description: trimmedPublicationDescription
      });
      updateTimestampMutation.mutate({ publicationId: publication.id, userId });
    } catch {
      // Error toast emitted by useUpdatePublication.
    }
  };

  return (
    <Dialog open={true} onClose={onClose} fullWidth maxWidth="sm" aria-labelledby="update-publication-dialog-title">
      <DialogTitle id="update-publication-dialog-title" sx={styles.dialogTitle}>
        {publicationModalMessages.editPublication}
        <IconButton aria-label="close" onClick={onClose} size="small" sx={styles.closeButton}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={styles.dialogContent}>
        <Box data-test="update-publication-form">
          <Box>
            <Box sx={styles.labelRow}>
              <Typography id="publication-name-label" variant="body2" sx={styles.formLabel}>
                {publicationModalMessages.publicationName}
              </Typography>
              <Typography variant="caption" sx={styles.characterCounter}>
                {publicationTitle.length}/{MAX_TITLE_LENGTH}
              </Typography>
            </Box>
            <TextField
              fullWidth
              size="small"
              sx={styles.textField}
              placeholder={publicationModalMessages.enterNamePlaceholder}
              value={publicationTitle}
              onChange={(e) => setPublicationTitle(e.target.value)}
              inputProps={{ 'data-test': 'publication-title-input', maxLength: MAX_TITLE_LENGTH, className: 'no-format', 'aria-labelledby': 'publication-name-label' }}
            />
          </Box>

          <Box sx={styles.listItem}>
            <Box sx={styles.labelRow}>
              <Typography id="publication-description-label" variant="body2" sx={styles.formLabel}>
                {publicationModalMessages.description}
              </Typography>
              <Typography variant="caption" sx={styles.characterCounter}>
                {publicationDescription?.length ?? 0}/{MAX_DESCRIPTION_LENGTH}
              </Typography>
            </Box>
            <TextField
              fullWidth
              size="small"
              multiline
              rows={2}
              sx={styles.textField}
              placeholder={publicationModalMessages.descriptionPlaceholer}
              value={publicationDescription}
              onChange={(e) => setPublicationDescription(e.target.value)}
              inputProps={{ 'data-test': 'publication-description-input', maxLength: MAX_DESCRIPTION_LENGTH, className: 'no-format', 'aria-labelledby': 'publication-description-label' }}
            />
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={styles.dialogActions}>
        <Button variant="outlined" color="secondary" size="small" onClick={onClose} data-test="cancel-update-publication-button">
          {publicationModalMessages.cancel}
        </Button>
        {isAdmin && (
          <Button
            variant="contained"
            size="small"
            color="primary"
            disabled={
              trimmedPublicationTitle.length < MIN_TITLE_LENGTH ||
              trimmedPublicationTitle.length > MAX_TITLE_LENGTH ||
              (trimmedPublicationDescription?.length ?? 0) > MAX_DESCRIPTION_LENGTH
            }
            onClick={() => {
              handleConfirmUpdatePublication();
            }}
            data-test="update-publication-button"
          >
            {publicationModalMessages.save}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
